import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../context/AnalyticsContext';
import ReminderModal from './modals/ReminderModal';

const Menu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [reminderModal, setReminderModal] = useState(false);
  const navigate = useNavigate();
  const { setAuth, user } = useAuth();
  const { setMyAnalytics } = useAnalytics();
  const location = useLocation();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('https://academichub-backend.onrender.com/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        setAuth(false);
        setMyAnalytics(null);
        navigate('/login');
      } else {
        console.error('Logout failed');
      }
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const menuLinkClass = (path: string) =>
    `flex items-center w-[90%] px-3 py-2 rounded-l-lg mx-auto border-l-4 ${
      isActive(path) ? 'bg-[#3c5b81] border-teal-300' : 'hover:bg-[#3c5b81] border-transparent'
    }`;

  const handleShareProfile = async () => {
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/importcode/new/${user?._id}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to share profile');
      }
      const data = await response.json();
      // Copy to clipboard
      await navigator.clipboard.writeText(data.code);
      alert('The shareable code has been saved to your clipboard: ' + data.code);
    } catch (error) {
      console.error('Error sharing profile:', error);
      alert('Failed to share profile. Please try again.');
    }
  };

  return (
    <>
      <button
        className={`md:hidden ${
          isOpen && 'hidden'
        } bg-white rounded-lg p-2 border fixed top-4 left-4 z-50`}
        onClick={toggleMenu}
      >
        <i className={`fa-solid text-xl fa-fw text-accent fa-bars`}></i>
      </button>

      <aside
        className={`z-50 w-64 h-screen bg-neutral text-white fixed top-0 left-0 transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:translate-x-0 md:static`}
      >
        <Link to="/" className="bg-primary flex justify-center p-2">
          <img src="/assets/logo.svg" alt="Logo" className="w-52 h-auto" />
        </Link>

        <nav>
          <ul className="space-y-2 text-center my-4">
            <li>
              <Link to="/" className={menuLinkClass('/')}>
                <i className="fa-solid fa-search fa-fw text-xs text-white mr-2" />
                Search Papers
              </Link>
            </li>

            <li>
              <div className="text-left px-4 text-sm font-semibold text-gray-300 mt-4 mb-1">
                Paper Management
              </div>
              <ul className="space-y-1 mt-1">
                <li>
                  <Link to="/papers/published" className={menuLinkClass('/papers/published')}>
                    <i className="fa-solid fa-circle-check fa-fw text-xs text-white mr-2" />
                    Published
                  </Link>
                </li>
                <li>
                  <Link to="/papers/unpublished" className={menuLinkClass('/papers/unpublished')}>
                    <i className="fa-solid fa-circle-exclamation fa-fw text-xs text-white mr-2" />
                    Unpublished
                  </Link>
                </li>
                <li>
                  <Link to="/manuscripts" className={menuLinkClass('/manuscripts')}>
                    <i className="fa-solid fa-file-lines fa-fw text-xs text-white mr-2" />
                    Manuscripts
                  </Link>
                </li>
              </ul>
            </li>

            {user?.academicType === 'professor' && (
              <li>
                <div className="text-left px-4 text-sm font-semibold text-gray-300 mt-4 mb-1">
                  Professor Tools
                </div>
                <ul className="space-y-1 mt-1">
                  <li>
                    <Link to="/students" className={menuLinkClass('/students')}>
                      <i className="fa-solid fa-people-group fa-fw text-xs text-white mr-2" />
                      My Students
                    </Link>
                  </li>
                  <li>
                    <Link to="/grants" className={menuLinkClass('/grants')}>
                      <i className="fa-solid fa-file-invoice-dollar fa-fw text-xs text-white mr-2" />
                      My Grants
                    </Link>
                  </li>
                  {user?.orcidId && (
                    <li>
                      <Link to="/analytics" className={menuLinkClass('/analytics')}>
                        <i className="fa-solid fa-chart-mixed fa-fw text-xs text-white mr-2" />
                        My Analytics
                      </Link>
                    </li>
                  )}
                </ul>
              </li>
            )}
          </ul>
        </nav>

        <div className="flex justify-between items-center gap-2 absolute bottom-0 w-full px-4 py-3 bg-neutral">
          <button
            onClick={() => setReminderModal(true)}
            className="text-sm bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            aria-label="Reminders"
          >
            <i className="fa-solid fa-bell"></i>
          </button>
          <button
            onClick={() => setIsUserModalOpen(true)}
            className="text-sm bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
            aria-label="User Info"
          >
            <i className="fa-solid fa-user text-white" />
          </button>
          <button
            onClick={handleLogout}
            className="text-sm bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
          >
            Log out
          </button>
        </div>
      </aside>

      {isOpen && (
        <div className="fixed inset-0 z-0 bg-black bg-opacity-50 md:hidden" onClick={toggleMenu} />
      )}

      {/* User Info Modal */}
      {isUserModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                User: <span className="text-blue-500">{user?.fullName}</span>
              </h2>
              <button onClick={() => setIsUserModalOpen(false)}>
                <i className="fa fa-x text-xs" />
              </button>
            </div>
            <div>
              <p>
                Email: <span className="text-blue-500">{user?.universityEmail}</span>
              </p>
              <p>
                ORCID ID: <span className="text-blue-500">{user?.orcidId}</span>
              </p>
              <p>
                Institution: <span className="text-blue-500">{user?.institutionName}</span>
              </p>
            </div>
            <button
              onClick={handleShareProfile}
              className="mt-4 text-sm bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
            >
              Share Profile
            </button>{' '}
          </div>
        </div>
      )}
      {/* Overlay for the modal */}
      {isUserModalOpen && (
        <div
          className="fixed inset-0 z-0 bg-black bg-opacity-50 md:hidden"
          onClick={() => setIsUserModalOpen(false)}
        />
      )}
      {reminderModal && <ReminderModal onClose={() => setReminderModal(false)} />}
    </>
  );
};

export default Menu;
